import { useEffect, useMemo, useRef, useState } from 'react';
import {
  readToneLabels,
  resetAllCustomTemplates,
  resetCustomTemplate,
  setCustomTemplate,
  setToneLabel,
  useClients,
  useClientStats,
  useCurrentUserProfile,
  useCustomTemplates,
  type ClientStatsView,
} from '@busy/api';
import {
  defaultToneLabel,
  TEMPLATE_LANGUAGES,
  TEMPLATE_TONES,
  TEMPLATE_VARIABLES,
  buildDraftVars,
  generateDraftText,
  systemTemplate,
  type TemplateLanguage,
  type TemplateTone,
  type TemplateVariable,
} from '@busy/core';
import { useToast } from '@busy/ui';
import { openSms } from '../../lib/sms';
import { translate } from '../../lib/i18n';

// All panel copy + variable chip labels are sourced from the central i18n
// dictionary under the `textTemplates.*` namespace. Most callers use the
// hook-based useT(), but THIS panel renders headings/labels in the
// language of the template being EDITED — not the user's UI language —
// so we resolve via translate(activeLang, key) instead.
function tInLang(lang: TemplateLanguage, key: string): string {
  return translate(lang, key);
}

// 3 tones × 2 languages = 6 editable templates total. Each tone has a single
// template body that applies to every client (no rotation). The displayed tone
// label ("Casual"/"Pro"/"Urgent") is also editable per barber and persists in
// profile.preferences.toneLabels.
export function TextTemplatesPanel() {
  const profile = useCurrentUserProfile();
  const clients = useClients();
  const stats = useClientStats();
  const customs = useCustomTemplates();
  const toast = useToast();

  const [activeLang, setActiveLang] = useState<TemplateLanguage>('en');
  // Local edit buffers — flushed on Save. drafts and labelDrafts are independent
  // so the user can edit either without committing the other.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [labelDrafts, setLabelDrafts] = useState<Partial<Record<TemplateTone, string>>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  // Index into clients[] for the live preview. Cycles via "Try a different client".
  const [previewIdx, setPreviewIdx] = useState(0);

  const textareaRefs = useRef<Record<TemplateTone, HTMLTextAreaElement | null>>({
    casual: null,
    pro: null,
    urgent: null,
  });
  const lastFocusedTone = useRef<TemplateTone | null>(null);

  // Server-state-driven labels — falls back to defaults when unset. Defaults are
  // language-aware: when the barber hasn't customized a tone, the picker reads in
  // whichever language they're currently editing in.
  const persistedLabels = useMemo(() => readToneLabels(profile.data ?? null), [profile.data]);
  function labelFor(tone: TemplateTone): string {
    if (labelDrafts[tone] != null) return labelDrafts[tone] ?? '';
    return persistedLabels[tone] ?? defaultToneLabel(tone, activeLang);
  }
  function isLabelDirty(tone: TemplateTone): boolean {
    return (
      labelDrafts[tone] !== undefined &&
      labelDrafts[tone] !== (persistedLabels[tone] ?? defaultToneLabel(tone, activeLang))
    );
  }

  // Reset edit buffers when server data lands
  useEffect(() => {
    if (!customs.data) return;
    setDrafts({});
  }, [customs.data]);

  const previewClient = useMemo(() => {
    const list = clients.data ?? [];
    if (list.length === 0) return null;
    return list[previewIdx % list.length];
  }, [clients.data, previewIdx]);

  const previewStats = useMemo(() => {
    if (!previewClient || !stats.data) return null;
    return stats.data.find((s: ClientStatsView) => s.client_id === previewClient.id) ?? null;
  }, [previewClient, stats.data]);

  function bodyFor(tone: TemplateTone): string {
    const key = `${tone}-${activeLang}`;
    if (drafts[key] !== undefined) return drafts[key];
    const custom = customs.data?.[tone]?.[activeLang];
    return custom ?? systemTemplate(tone, activeLang);
  }

  function setBody(tone: TemplateTone, body: string) {
    const key = `${tone}-${activeLang}`;
    setDrafts((prev) => ({ ...prev, [key]: body }));
  }

  function isCustomized(tone: TemplateTone): boolean {
    return !!customs.data?.[tone]?.[activeLang];
  }

  function isDirty(tone: TemplateTone): boolean {
    const key = `${tone}-${activeLang}`;
    if (drafts[key] === undefined) return false;
    const current = customs.data?.[tone]?.[activeLang] ?? systemTemplate(tone, activeLang);
    return drafts[key] !== current;
  }

  async function handleSave(tone: TemplateTone) {
    const labelDirty = isLabelDirty(tone);
    const bodyDirty = isDirty(tone);
    if (!labelDirty && !bodyDirty) return;
    setSavingKey(`save-${tone}`);

    if (bodyDirty) {
      const body = drafts[`${tone}-${activeLang}`];
      const { error } = await setCustomTemplate(tone, activeLang, body);
      if (error) {
        toast(`Couldn't save body: ${error.message}`, 'error');
        setSavingKey(null);
        return;
      }
    }
    if (labelDirty) {
      const label = labelDrafts[tone] ?? '';
      const { error } = await setToneLabel(tone, label);
      if (error) {
        toast(`Couldn't save label: ${error.message}`, 'error');
        setSavingKey(null);
        return;
      }
      setLabelDrafts((prev) => {
        const next = { ...prev };
        delete next[tone];
        return next;
      });
    }
    setSavingKey(null);
    toast('Saved', 'success');
  }

  async function handleReset(tone: TemplateTone) {
    if (
      !confirm(
        `Reset "${labelFor(tone)}" template back to the default? Body resets in ${tInLang(
          activeLang,
          `textTemplates.lang.${activeLang}`,
        )} only; the tone label resets to "${defaultToneLabel(tone, activeLang)}".`,
      )
    )
      return;
    setSavingKey(`reset-${tone}`);
    const tasks: Promise<{ error: { message: string } | null }>[] = [];
    if (isCustomized(tone)) tasks.push(resetCustomTemplate(tone, activeLang));
    if (persistedLabels[tone] !== undefined) tasks.push(setToneLabel(tone, ''));
    const results = await Promise.all(tasks);
    setSavingKey(null);
    const firstErr = results.find((r) => r.error);
    if (firstErr?.error) {
      toast(`Couldn't reset: ${firstErr.error.message}`, 'error');
      return;
    }
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[`${tone}-${activeLang}`];
      return next;
    });
    setLabelDrafts((prev) => {
      const next = { ...prev };
      delete next[tone];
      return next;
    });
    toast('Reset to default', 'success');
  }

  async function handleResetAll() {
    if (!confirm('Reset all customized templates and tone names back to the defaults? Your edits across every tone and language will be deleted.')) return;
    const { error: tplErr } = await resetAllCustomTemplates();
    if (tplErr) {
      toast(`Couldn't reset templates: ${tplErr.message}`, 'error');
      return;
    }
    // Reset tone labels: clear each via empty string
    const labelResets = await Promise.all(
      TEMPLATE_TONES.map((t) => setToneLabel(t, ''))
    );
    const labelErr = labelResets.find((r) => r.error);
    if (labelErr?.error) {
      toast(`Templates reset, but labels failed: ${labelErr.error.message}`, 'error');
      return;
    }
    setDrafts({});
    setLabelDrafts({});
    toast('All templates reset', 'success');
  }

  function handleInsertVar(varName: TemplateVariable) {
    const tone = lastFocusedTone.current;
    if (tone == null) {
      toast('Tap inside a template box first, then tap a variable to insert.', 'info');
      return;
    }
    const ta = textareaRefs.current[tone];
    if (!ta) return;
    const start = ta.selectionStart ?? ta.value.length;
    const end = ta.selectionEnd ?? ta.value.length;
    const before = ta.value.slice(0, start);
    const after = ta.value.slice(end);
    const insertion = `{${varName}}`;
    const next = before + insertion + after;
    setBody(tone, next);
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + insertion.length;
      try {
        ta.setSelectionRange(pos, pos);
      } catch {
        /* ignore */
      }
    });
  }

  function buildPreview(tone: TemplateTone): string {
    if (!previewClient) {
      return 'Add a client to see a preview filled with their data.';
    }
    const body = bodyFor(tone);
    const vars = buildDraftVars({
      clientId: previewClient.id,
      firstName: previewClient.first_name,
      daysSinceLastVisit:
        previewStats?.days_since_last_visit != null
          ? Math.round(previewStats.days_since_last_visit)
          : null,
      typicalGap: previewStats?.cycle_days != null ? Math.round(previewStats.cycle_days) : null,
      preferredService: null,
      signature:
        profile.data?.first_name?.trim() || (activeLang === 'es' ? '[tu nombre]' : '[your name]'),
      language: activeLang,
    });
    // Force-substitute against this exact body (not the engine's resolved one)
    return generateDraftText({
      tone,
      language: activeLang,
      vars,
      customLookup: () => body,
    });
  }

  function handleTestSend(tone: TemplateTone) {
    const userPhone = profile.data?.phone?.trim();
    if (!userPhone) {
      toast('Add your phone number in Settings → Profile to test-send to yourself.', 'error');
      return;
    }
    if (!previewClient) {
      toast('Need at least one client on the roster to render a preview.', 'error');
      return;
    }
    openSms(userPhone, buildPreview(tone));
  }

  // Local helper so the JSX below keeps reading short (`copy('X')` vs.
  // `translate(activeLang, 'textTemplates.X')`).
  const copy = (k: string) => tInLang(activeLang, `textTemplates.${k}`);

  return (
    <div className="space-y-4">
      {/* Language toggle */}
      <div>
        <div className="text-[11px] tracking-[0.14em] uppercase font-extrabold text-ink-500 mb-2">
          {copy('languageHeading')}
        </div>
        <div className="grid grid-cols-2 gap-2">
          {TEMPLATE_LANGUAGES.map((lang) => {
            const active = activeLang === lang;
            return (
              <button
                key={lang}
                type="button"
                onClick={() => setActiveLang(lang)}
                className={`h-10 rounded-lg font-semibold text-sm transition-colors ${
                  active
                    ? 'bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900'
                    : 'bg-bg-alt text-ink-700 hover:bg-line'
                }`}
              >
                {tInLang(activeLang, `textTemplates.lang.${lang}`)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Variable chips */}
      <div className="rounded-xl border border-line bg-bg-alt p-3">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="text-[11px] tracking-[0.14em] uppercase font-extrabold text-ink-500">
            {copy('variablesHeading')}
          </div>
          <span className="text-[11px] text-ink-500">{copy('chipHelper')}</span>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {TEMPLATE_VARIABLES.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => handleInsertVar(v)}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-line bg-surface text-[12px] font-bold text-ink-700 hover:border-navy-900 dark:hover:border-gold-500 transition-colors"
            >
              <code className="text-[11px] text-ink-500">{`{${v}}`}</code>
              <span>·</span>
              <span>{tInLang(activeLang, `textTemplates.var.${v}`)}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Preview-client header */}
      <div className="rounded-xl bg-bg-alt border border-line p-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-[12px] text-ink-700">
            <span className="text-[10px] tracking-[0.14em] uppercase font-extrabold text-ink-500 mr-2">
              {copy('previewLabel')}
            </span>
            <strong>
              {previewClient
                ? `${previewClient.first_name} ${previewClient.last_name}`.trim() || 'Unnamed'
                : copy('noClientsYet')}
            </strong>
          </div>
          <button
            type="button"
            onClick={() => setPreviewIdx((i) => i + 1)}
            disabled={!clients.data || clients.data.length < 2}
            className="text-[11px] font-bold uppercase tracking-wide text-navy-900 dark:text-gold-500 disabled:opacity-40 hover:underline"
          >
            {copy('tryDifferentClient')}
          </button>
        </div>
      </div>

      {/* Three tone cards — one per tone, editable label + body */}
      <div className="space-y-3">
        {TEMPLATE_TONES.map((tone) => {
          const customized = isCustomized(tone);
          const bodyDirty = isDirty(tone);
          const labelDirty = isLabelDirty(tone);
          const dirty = bodyDirty || labelDirty;
          const isSaving = savingKey === `save-${tone}`;
          const isResetting = savingKey === `reset-${tone}`;
          const labelOverridden = persistedLabels[tone] !== undefined;

          return (
            <div
              key={tone}
              className={`rounded-xl border bg-surface overflow-hidden ${
                dirty
                  ? 'border-gold-500/60'
                  : customized || labelOverridden
                  ? 'border-navy-900/30 dark:border-gold-500/40'
                  : 'border-line'
              }`}
            >
              <div className="px-4 py-3 border-b border-line flex items-center justify-between gap-2">
                <input
                  type="text"
                  value={labelFor(tone)}
                  onChange={(e) =>
                    setLabelDrafts((prev) => ({ ...prev, [tone]: e.target.value }))
                  }
                  placeholder={defaultToneLabel(tone, activeLang)}
                  aria-label={`${defaultToneLabel(tone, activeLang)} tone label`}
                  className="font-display text-xl text-navy-900 dark:text-gold-500 bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-navy-900 dark:focus:ring-gold-500 rounded px-1 -mx-1 min-w-0 flex-1"
                />
                <div className="flex items-center gap-1.5 shrink-0">
                  {(customized || labelOverridden) && !dirty && (
                    <span className="bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 text-[9px] font-extrabold tracking-wide uppercase px-1.5 py-0.5 rounded-full">
                      {copy('customized')}
                    </span>
                  )}
                  {dirty && (
                    <span className="bg-gold-500 text-navy-900 text-[9px] font-extrabold tracking-wide uppercase px-1.5 py-0.5 rounded-full">
                      {copy('unsaved')}
                    </span>
                  )}
                </div>
              </div>

              <div className="p-3 space-y-2">
                <textarea
                  ref={(el) => {
                    textareaRefs.current[tone] = el;
                  }}
                  value={bodyFor(tone)}
                  onChange={(e) => setBody(tone, e.target.value)}
                  onFocus={() => {
                    lastFocusedTone.current = tone;
                  }}
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg border border-line bg-surface text-ink-900 text-[14px] leading-relaxed focus:outline-none focus:ring-2 focus:ring-navy-900 dark:focus:ring-gold-500 resize-none font-mono"
                />

                <div className="rounded-lg bg-bg-alt border border-line/50 p-2.5 text-[13px] text-ink-700 leading-relaxed">
                  <div className="text-[10px] tracking-[0.14em] uppercase font-extrabold text-ink-500 mb-1">
                    {copy('previewHeading')}
                  </div>
                  {buildPreview(tone)}
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleSave(tone)}
                    disabled={!dirty || isSaving}
                    className="flex-1 h-10 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-semibold text-sm disabled:opacity-40"
                  >
                    {isSaving ? copy('saving') : dirty ? copy('saveChanges') : copy('saved')}
                  </button>
                  {(customized || labelOverridden || dirty) && (
                    <button
                      type="button"
                      onClick={() => handleReset(tone)}
                      disabled={isResetting}
                      className="px-3 h-10 rounded-lg border border-line bg-surface text-sm font-semibold text-ink-700 hover:bg-bg-alt disabled:opacity-50"
                    >
                      {isResetting ? '…' : copy('reset')}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleTestSend(tone)}
                    className="px-3 h-10 rounded-lg border border-line bg-surface text-sm font-semibold text-ink-700 inline-flex items-center gap-1.5 hover:bg-bg-alt"
                  >
                    <SendIcon className="w-3.5 h-3.5" />
                    {copy('testSend')}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="border-t border-line pt-4 flex items-center justify-between gap-2 flex-wrap">
        <p className="text-[12px] text-ink-500 leading-relaxed flex-1 min-w-[200px]">
          {copy('testSendHelper')}
        </p>
        <button
          type="button"
          onClick={handleResetAll}
          className="text-[12px] font-bold text-red-600 dark:text-red-400 hover:underline"
        >
          {copy('resetAll')}
        </button>
      </div>
    </div>
  );
}

function SendIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}
